import { provideHttpClient, withInterceptors } from '@angular/common/http';
import {
  ApplicationConfig,
  importProvidersFrom,
  inject,
  isDevMode,
  provideAppInitializer
} from '@angular/core';
import { MatBottomSheetModule } from '@angular/material/bottom-sheet';
import {
  DateAdapter,
  MAT_DATE_LOCALE,
  MatNativeDateModule
} from '@angular/material/core';
import { MatDialogModule } from '@angular/material/dialog';
import {
  MAT_TOOLTIP_DEFAULT_OPTIONS,
  MatTooltipDefaultOptions
} from '@angular/material/tooltip';
import { BrowserModule } from '@angular/platform-browser';
import { provideAnimations } from '@angular/platform-browser/animations';
import { provideRouter, Router, withInMemoryScrolling } from '@angular/router';
import { provideServiceWorker } from '@angular/service-worker';
import { NgxsReduxDevtoolsPluginModule } from '@ngxs/devtools-plugin';
import { NgxsLoggerPluginModule } from '@ngxs/logger-plugin';
import { NgxsStoragePluginModule } from '@ngxs/storage-plugin';
import { NgxsModule, Store } from '@ngxs/store';
import {
  authSessionInterceptor,
  AuthSessionService,
  DEFAULT_CURRENCY,
  DEFAULT_LOCALE,
  environment,
  ENVIRONMENT_CONFIG_TOKEN,
  navigateToLogin,
  PHONE_NUMBER_INPUT_CONFIG,
  PhoneNumberConfigService,
  TENANT_API,
  provideAuthSession,
  provideIcons,
  provideLuxon,
  provideStorefrontUrl
} from '@pharvio/core/shared';
import {
  AlertModule,
  AlertService,
  ANALYTICS_APP,
  loadingInterceptor,
  LocaleDateAdapter,
  provideNavigation,
  provideSessionExpiry,
  provideVex,
  VEX_USER_PROFILE,
  vexConfigs
} from '@pharvio/core/ui';
import { provideQuillConfig } from 'ngx-quill';
import { of } from 'rxjs';
import { catchError, map, take } from 'rxjs/operators';
import { APP_ROUTES } from './app-routes';
import { appRoutes } from './app.routes';
import { AnalyticsBootstrapService } from './core/analytics/analytics-bootstrap.service';
import { provideDataServices } from './core/data-providers';
import { NavigationLoaderService, NavigationService } from './core/navigation';
import { PwaCacheService } from './core/services/pwa-cache.service';
import { PwaInstallService } from './core/services/pwa-install.service';
import { PwaUpdateService } from './core/services/pwa-update.service';
import { VexUserProfileService } from './core/services/vex-user-profile.service';

import {
  AuthState,
  ClearAuth,
  Logout,
  RefreshToken
} from '@pharvio/accounts/admin-account/data-access';
import { AssistantState } from '@pharvio/assistant/data-access';
import { BranchSuperintendentsState } from '@pharvio/branch-superintendents/data-access';
import { BranchesState } from '@pharvio/branches/data-access';
import {
  BatchesState,
  CatalogImportState,
  CategoriesState,
  FeaturedProductsState,
  ProductsState,
  SuppliersState,
  TagsState
} from '@pharvio/catalog/data-access';
import {
  BookableServicesState,
  BookingsState
} from '@pharvio/bookings/data-access';
import { CurrencyState } from '@pharvio/currency/data-access';
import {
  ExpenseCategoriesState,
  ExpenseImportState,
  ExpenseSchedulesState,
  ExpensesState,
  PayeesState
} from '@pharvio/expenses/data-access';
import { CustomersState } from '@pharvio/customers/data-access';
import { TenantDashboardState } from '@pharvio/dashboard/data-access';
import {
  GrnState,
  PurchaseOrdersState,
  RecallsState,
  StockState,
  StockTakesState,
  TransfersState
} from '@pharvio/inventory/data-access';
import { LoyaltyState } from '@pharvio/loyalty/data-access';
import { OfflineState, provideOfflineMode } from '@pharvio/offline';
import { OrdersState, ReturnsState } from '@pharvio/orders/data-access';
import { PayoutAccountsState } from '@pharvio/payouts/data-access';
import {
  PosState,
  RefundTicketsState,
  ShiftsState
} from '@pharvio/pos/data-access';
import { PrescriptionsState } from '@pharvio/prescriptions/data-access';
import { ReportsState } from '@pharvio/reports/data-access';
import {
  FeaturesState,
  SettingsState,
  TaxClassesState
} from '@pharvio/settings/data-access';
import { stepUpInterceptor, StepUpState } from '@pharvio/step-up';
import {
  StorefrontAvailabilityState,
  StorefrontSettingsState
} from '@pharvio/storefront/data-access';
import { TenantOrganisationState } from '@pharvio/tenant-organisation/data-access';
import { UserCredentialsState } from '@pharvio/user-credentials/data-access';
import { RolesState, UsersState } from '@pharvio/users/data-access';
import {
  errorInterceptor,
  responseInterceptor,
  retryInterceptor,
  tenantInterceptor
} from './core/interceptors';
import { AuditLogState } from './features/audit-log/state/audit-log.state';

export const appConfig: ApplicationConfig = {
  providers: [
    // Boot default for every Material datepicker. The tenant's real locale
    // arrives with the storefront settings, after bootstrap, so the shell
    // calls `DateAdapter.setLocale` once it lands — see LayoutComponent.
    { provide: MAT_DATE_LOCALE, useValue: DEFAULT_CURRENCY.locale },
    /**
     * No tooltips from touch.
     *
     * Material's default is `touchGestures: 'auto'`, which shows a tooltip on
     * long press and, on anything it reads as non-interactive, suppresses the
     * browser's own touch gestures to do it. On a phone that means tooltips
     * firing while someone scrolls a table, and text they can no longer select.
     *
     * A tooltip is a pointer affordance. Anything a touch user actually needs
     * has to be on screen or behind a tap, never behind a hover — so an
     * icon-only control needs a visible label or a bottom sheet at small
     * widths, not a tooltip that will never open.
     */
    {
      provide: MAT_TOOLTIP_DEFAULT_OPTIONS,
      useValue: {
        showDelay: 0,
        hideDelay: 0,
        touchendHideDelay: 3000,
        touchLongPressShowDelay: 100,
        touchGestures: 'auto'
      } satisfies MatTooltipDefaultOptions
    },
    importProvidersFrom(
      BrowserModule,
      MatDialogModule,
      MatBottomSheetModule,
      MatNativeDateModule,
      AlertModule,
      NgxsModule.forRoot(
        [
          AuthState,
          UsersState,
          RolesState,
          BranchesState,
          CategoriesState,
          TagsState,
          SuppliersState,
          BatchesState,
          TenantDashboardState,
          CustomersState,
          PurchaseOrdersState,
          GrnState,
          StockState,
          StockTakesState,
          TransfersState,
          RecallsState,
          PosState,
          RefundTicketsState,
          ShiftsState,
          LoyaltyState,
          ReportsState,
          PrescriptionsState,
          OrdersState,
          ReturnsState,
          AssistantState,
          SettingsState,
          TaxClassesState,
          FeaturesState,
          StorefrontSettingsState,
          StorefrontAvailabilityState,
          ProductsState,
          FeaturedProductsState,
          CurrencyState,
          TenantOrganisationState,
          BranchSuperintendentsState,
          UserCredentialsState,
          AuditLogState,
          StepUpState,
          PayoutAccountsState,
          OfflineState,
          CatalogImportState,
          ExpensesState,
          ExpenseCategoriesState,
          PayeesState,
          ExpenseSchedulesState,
          ExpenseImportState,
          BookableServicesState,
          BookingsState
        ],
        {
          developmentMode: !environment.production && !environment.staging
        }
      ),
      NgxsLoggerPluginModule.forRoot({
        disabled: environment.production || environment.staging
      }),
      NgxsReduxDevtoolsPluginModule.forRoot(),
      NgxsStoragePluginModule.forRoot({
        // Never persist the access token — it lives in memory only; the
        // session is restored on boot from the httpOnly refresh cookie.
        // `pos.*` keep carts across reloads (offline tills); `storefront.settings`
        // gives pay-by-QR its tenant slug/domain without a request.
        keys: [
          'auth.currentBranchId',
          'currency',
          'pos.carts',
          'pos.activeCartId',
          'storefront.settings',
          // Feature gating decides nav visibility and catalog wording, so an
          // offline cold start must not come up with an empty map and flip
          // every gate at once.
          'features.tenant'
        ]
      })
    ),
    {
      provide: ENVIRONMENT_CONFIG_TOKEN,
      useValue: environment
    },
    // All three apps build from the same environment.ts, so this is the only
    // thing separating ERP events from storefront/platform-admin ones.
    { provide: ANALYTICS_APP, useValue: 'erp' },
    {
      provide: PHONE_NUMBER_INPUT_CONFIG,
      useExisting: PhoneNumberConfigService
    },
    {
      // Default locale
      provide: DEFAULT_LOCALE,
      useFactory: () =>
        inject(Store).selectSnapshot(CurrencyState.currency).locale
    },
    provideRouter(
      appRoutes,
      withInMemoryScrolling({
        anchorScrolling: 'enabled',
        scrollPositionRestoration: 'enabled'
      })
    ),
    provideAnimations(),
    provideHttpClient(
      withInterceptors([
        loadingInterceptor,
        authSessionInterceptor,
        tenantInterceptor,
        stepUpInterceptor,
        responseInterceptor,
        retryInterceptor,
        errorInterceptor
      ])
    ),
    provideAppInitializer(() => {
      inject(AnalyticsBootstrapService).init();
      inject(PHONE_NUMBER_INPUT_CONFIG).loadAllowedCountries();
    }),
    // Session restore at boot (cookie refresh online / offline mode otherwise)
    // is owned by provideOfflineMode() below.

    provideAuthSession(() => {
      const store = inject(Store);
      const router = inject(Router);
      const alert = inject(AlertService);
      const credentialEndpoints: string[] = [
        TENANT_API.AUTH.LOGIN,
        TENANT_API.AUTH.MFA_VERIFY,
        TENANT_API.AUTH.REFRESH,
        TENANT_API.AUTH.LOGOUT,
        TENANT_API.AUTH.FORGOT_PASSWORD,
        TENANT_API.AUTH.RESET_PASSWORD,
        TENANT_API.INVITATIONS.VALIDATE,
        TENANT_API.INVITATIONS.ACCEPT
      ];
      return {
        lockName: 'pharvio-erp-auth-refresh',
        getAccessToken: () => store.selectSnapshot(AuthState.token),
        refresh: () =>
          store.dispatch(new RefreshToken()).pipe(
            map(() => !!store.selectSnapshot(AuthState.token)),
            catchError(() => of(false))
          ),
        isAuthEndpoint: (url) =>
          credentialEndpoints.some((endpoint) => url.startsWith(endpoint)),
        onSessionLost: (reason) => {
          const fromUrl = router.url;
          store.dispatch(new ClearAuth());
          alert.info(
            reason === 'mfa_pending'
              ? 'Your sign-in was incomplete. Please sign in again.'
              : 'Your session has ended. Please sign in again.'
          );
          navigateToLogin(router, [APP_ROUTES.Auth], fromUrl);
        }
      };
    }),

    ...provideDataServices(),

    provideSessionExpiry(() => {
      const store = inject(Store);
      const router = inject(Router);
      const alert = inject(AlertService);
      const session = inject(AuthSessionService);
      const signOut = (expired: boolean) => {
        const fromUrl = router.url;
        if (expired) {
          alert.info('Your session has expired. Please sign in again.');
        }
        store
          .dispatch(new Logout())
          .pipe(take(1))
          .subscribe({
            complete: () => navigateToLogin(router, [APP_ROUTES.Auth], fromUrl)
          });
      };
      const endSession = () => signOut(true);
      return {
        token$: store.select(AuthState.token),
        // The refresh token is an httpOnly cookie we can't inspect — always
        // offer "Stay signed in"; a failed refresh ends the session.
        canRefresh: () => true,
        onStaySignedIn: () =>
          session.refresh().subscribe((restored) => {
            if (!restored) endSession();
          }),
        onSessionEnd: endSession,
        onLogout: () => signOut(false)
      };
    }),
    provideStorefrontUrl(() => {
      const store = inject(Store);
      const storefrontSettings$ = store.select(
        StorefrontSettingsState.settings
      );
      // const customDomain = storefrontSettings?.customDomain ?? null;
      // const slug = storefrontSettings?.slug;
      return storefrontSettings$.pipe(
        map((settings) => {
          const customDomain = settings?.customDomain ?? null;
          const slug = settings?.slug ?? '';
          return {
            customDomain,
            slug,
            acceptsOnlineOrders: settings?.acceptsOnlineOrders ?? false
          };
        })
      );
    }),

    ...provideVex({
      config: vexConfigs.poseidon,
      availableThemes: [
        { name: 'Default', className: 'vex-theme-default' },
        { name: 'Teal', className: 'vex-theme-teal' },
        { name: 'Green', className: 'vex-theme-green' },
        { name: 'Purple', className: 'vex-theme-purple' },
        { name: 'Red', className: 'vex-theme-red' },
        { name: 'Orange', className: 'vex-theme-orange' }
      ]
    }),
    { provide: VEX_USER_PROFILE, useExisting: VexUserProfileService },
    ...provideNavigation(NavigationLoaderService, NavigationService),
    ...provideIcons(),
    ...provideLuxon(),
    provideQuillConfig({
      modules: {
        toolbar: [
          ['bold', 'italic', 'underline', 'strike'],
          ['blockquote', 'code-block'],
          [{ list: 'ordered' }, { list: 'bullet' }],
          [{ header: [1, 2, 3, 4, 5, 6, false] }],
          ['clean', 'link']
        ]
      }
    }),
    provideServiceWorker('ngsw-worker.js', {
      enabled: !isDevMode(),
      // enabled: true,
      registrationStrategy: 'registerWhenStable:30000'
    }),
    provideAppInitializer(() => {
      inject(PwaUpdateService).init();
      inject(PwaInstallService).init();
      inject(PwaCacheService).init();
    }),
    provideOfflineMode({
      routes: {
        dashboard: `/${APP_ROUTES.Dashboard}`,
        pos: `/${APP_ROUTES.POS}`,
        orders: `/${APP_ROUTES.Orders}`,
        createOrder: `/${APP_ROUTES.Orders}/create`,
        unavailable: `/${APP_ROUTES.OfflineUnavailable}`
      }
    }),
    // Must come after `MatNativeDateModule` above, which binds `DateAdapter`
    // to the plain `NativeDateAdapter` — the later provider wins the token.
    // Only `parse` differs, so this is a drop-in: every datepicker still
    // reads and writes a native `Date`.
    { provide: DateAdapter, useClass: LocaleDateAdapter }
  ]
};
